import { Component } from '@theme/component';
import { CartUpdateEvent } from '@theme/events';

/**
 * Offer banner with a live countdown and an add-to-cart CTA.
 *
 * Timer modes (via `data-mode`), same contract as assets/countdown-banner.js so
 * both sections read a campaign the same way:
 *  - `fixed`: counts down to the Brazilian-formatted date in `data-end`.
 *  - `evergreen`: counts down `data-minutes` from the visitor's first view.
 *
 * When the timer reaches zero, `data-on-complete` decides between freezing at
 * 00:00:00 (`stop`) or starting a fresh evergreen cycle (`restart`).
 *
 * Everything price-related (discount, installments, free shipping) is resolved
 * in Liquid — this component never parses formatted money.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [hours]
 * @property {HTMLElement} [minutes]
 * @property {HTMLElement} [seconds]
 * @property {HTMLElement} [copyLabel]
 *
 * @extends {Component<Refs>}
 */
export class PromoBannerCountdown extends Component {
  /** @type {number|undefined} */
  #interval = undefined;

  /** @type {number|undefined} */
  #copyTimeout = undefined;

  /** @type {number} Deadline as epoch milliseconds. */
  #deadline = 0;

  /** @type {boolean} Guards against double submits while a cart request is in flight. */
  #busy = false;

  connectedCallback() {
    super.connectedCallback();

    this.#deadline = this.#resolveDeadline();
    this.#tick();
    this.#interval = setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    clearInterval(this.#interval);
    clearTimeout(this.#copyTimeout);
  }

  // --------------------------------------------------------------- countdown

  get #storageKey() {
    return `promo-banner-countdown:${this.id || this.dataset.sectionId || 'default'}`;
  }

  /**
   * @returns {number} epoch milliseconds for the countdown target.
   */
  #resolveDeadline() {
    // The End date is a hard campaign stop in both modes.
    const endDate = this.#parseBrDate(this.dataset.end ?? '');

    if (this.dataset.mode !== 'evergreen') return endDate;

    const minutes = parseInt(this.dataset.minutes ?? '0', 10) || 0;
    const key = `${this.#storageKey}:start`;
    let start = 0;

    try {
      start = parseInt(localStorage.getItem(key) ?? '0', 10);
      if (!start) {
        start = Date.now();
        localStorage.setItem(key, String(start));
      }
    } catch {
      // Private mode / storage disabled — the timer just restarts every visit.
      start = Date.now();
    }

    const evergreenDeadline = start + minutes * 60_000;

    // Never let the per-visit timer run past the campaign's End date.
    return endDate ? Math.min(evergreenDeadline, endDate) : evergreenDeadline;
  }

  /**
   * Starts a new evergreen cycle from now, replacing the stored start time.
   */
  #restartEvergreen() {
    const minutes = parseInt(this.dataset.minutes ?? '0', 10) || 0;
    if (!minutes) return;

    const start = Date.now();
    try {
      localStorage.setItem(`${this.#storageKey}:start`, String(start));
    } catch {
      // Nothing to persist — the cycle still restarts for this page view.
    }
    this.#deadline = start + minutes * 60_000;
  }

  /**
   * Parses a Brazilian-formatted date string into epoch ms (visitor local time).
   * Accepts "DD/MM/AAAA", "DD/MM/AAAA HH:MM" or "DD/MM/AAAA HH:MM:SS".
   *
   * @param {string} value
   * @returns {number} epoch ms, or 0 when invalid.
   */
  #parseBrDate(value) {
    const raw = value.trim();
    if (!raw) return 0;

    const [datePart = '', timePart = ''] = raw.split(/[ T]+/);
    const [day, month, year] = datePart.split('/').map(Number);
    const [hours = 0, minutes = 0, seconds = 0] = timePart.split(':').map(Number);

    if (!day || !month || !year) return 0;

    const date = new Date(year, month - 1, day, hours, minutes, seconds);
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
  }

  #tick() {
    if (!this.#deadline) {
      // No valid deadline configured — leave the placeholder digits alone.
      clearInterval(this.#interval);
      return;
    }

    // A finished evergreen cycle rolls straight into the next one, so the digits
    // never sit at 00:00:00 for a frame before the restart lands.
    if (
      this.#deadline - Date.now() <= 0 &&
      this.dataset.onComplete === 'restart' &&
      this.dataset.mode === 'evergreen'
    ) {
      this.#restartEvergreen();
    }

    const diff = Math.max(0, this.#deadline - Date.now());
    const totalSeconds = Math.floor(diff / 1000);
    const { hours, minutes, seconds } = this.refs;

    // No days unit in this layout — days roll into hours so the timer stays accurate.
    this.#paint(hours, Math.floor(totalSeconds / 3600));
    this.#paint(minutes, Math.floor((totalSeconds % 3600) / 60));
    this.#paint(seconds, totalSeconds % 60);

    if (diff <= 0) clearInterval(this.#interval);
  }

  /**
   * @param {HTMLElement|undefined} element
   * @param {number} value
   */
  #paint(element, value) {
    if (element) element.textContent = String(value).padStart(2, '0');
  }

  // ------------------------------------------------------------------ coupon

  /**
   * Copies the coupon code and flips the button label for a couple of seconds.
   */
  async copyCoupon() {
    const code = this.dataset.couponCode;
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code);
    } catch {
      // Insecure context / permission denied — fall back to the legacy path.
      this.#legacyCopy(code);
    }

    this.#flashCopied();
  }

  /**
   * @param {string} code
   */
  #legacyCopy(code) {
    const field = document.createElement('textarea');
    field.value = code;
    field.setAttribute('readonly', '');
    field.style.cssText = 'position:fixed;left:-9999px;opacity:0';
    document.body.appendChild(field);
    field.select();
    try {
      document.execCommand('copy');
    } catch {
      // Nothing left to try — the code stays visible on screen for manual copy.
    }
    field.remove();
  }

  #flashCopied() {
    const { copyLabel } = this.refs;
    if (!copyLabel) return;

    const { copyLabel: idle = '', copiedLabel: done = '' } = this.dataset;
    copyLabel.textContent = done;
    clearTimeout(this.#copyTimeout);
    this.#copyTimeout = setTimeout(() => {
      copyLabel.textContent = idle;
    }, 2000);
  }

  // -------------------------------------------------------------------- cart

  /**
   * Adds the offer's variant to the cart. Uses `/cart/add.js` + `CartUpdateEvent`
   * — the theme's contract — so the drawer opens, the header count updates and
   * the Yampi checkout keeps owning `/cart` without anything banner-specific.
   *
   * @param {Event} event
   */
  async addToCart(event) {
    event.preventDefault();

    const id = Number(this.dataset.variantId);
    if (!id || this.#busy) return;

    this.#busy = true;
    this.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
      });
      const payload = await response.json();

      if (!response.ok || payload.status || payload.errors) {
        throw new Error(payload.description || payload.message || 'Cart add failed');
      }

      const cart = await (await fetch('/cart.js')).json();
      document.dispatchEvent(
        new CartUpdateEvent(cart, this.id, {
          source: 'promo-banner-countdown',
          itemCount: cart?.item_count ?? 0,
          variantId: String(id),
        })
      );
    } catch (error) {
      console.error(error);
      window.location.href = '/cart';
    } finally {
      this.#busy = false;
      this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('promo-banner-countdown')) {
  customElements.define('promo-banner-countdown', PromoBannerCountdown);
}
